import type {
  AreaResult,
  GeocodeResult,
  PagedResult,
  PlaceDetail,
  PlaceGroup,
  PlaceKind,
  PlaceStats,
  PlaceSummary,
  PlannedRoute,
  ProblemDetails,
  RouteDetail,
  RouteSummary,
  SharedTrip,
  TripKind,
  Waypoint,
} from './types';

const BASE = import.meta.env.VITE_API_BASE ?? '';

export class ApiError extends Error {
  status: number;
  problem: ProblemDetails | null;

  constructor(status: number, problem: ProblemDetails | null) {
    super(problem?.title ?? `Request failed (${status})`);
    this.status = status;
    this.problem = problem;
  }

  /** Field errors keyed by camelCase property name. */
  get fieldErrors(): Record<string, string[]> {
    return this.problem?.errors ?? {};
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { Accept: 'application/json', ...(init?.body ? { 'Content-Type': 'application/json' } : {}), ...init?.headers },
  });

  if (!res.ok) {
    let problem: ProblemDetails | null = null;
    try {
      problem = await res.json();
    } catch {
      /* not JSON */
    }
    if (res.status === 429) {
      problem = { ...problem, title: 'Slow down a little. Too many requests. Try again in a minute.' };
    }
    throw new ApiError(res.status, problem);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

function qs(params: Record<string, string | number | boolean | string[] | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    if (Array.isArray(value)) value.forEach((v) => sp.append(key, v));
    else sp.set(key, String(value));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export interface PlaceSearchParams {
  q?: string;
  group?: PlaceGroup;
  state?: string;
  town?: string;
  tags?: string[];
  openOnDay?: number;
  minScore?: number;
  curatedOnly?: boolean;
  nearLat?: number;
  nearLng?: number;
  radiusMiles?: number;
  bounds?: [number, number, number, number];
  sort?: 'score' | 'name' | 'distance';
  page?: number;
  pageSize?: number;
}

export interface CorridorFilters {
  group?: PlaceGroup;
  minScore: number;
  tags: string[];
  openOnDay?: number;
}

export const api = {
  stats: () => request<PlaceStats>('/api/places/stats'),

  // "all" is the API's default, so it stays off the query string.
  searchPlaces: (p: PlaceSearchParams) =>
    request<PagedResult<PlaceSummary>>(
      `/api/places${qs({ ...p, group: p.group === 'all' ? undefined : p.group, bounds: p.bounds?.map((n) => n.toFixed(4)).join(',') })}`,
    ),

  place: (slug: string) => request<PlaceDetail>(`/api/places/${encodeURIComponent(slug)}`),

  suggestPlace: (body: {
    name: string;
    kind?: PlaceKind;
    town: string;
    state: string;
    address?: string;
    whyItsGreat?: string;
    submitterName?: string;
    submitterEmail?: string;
  }) => request<{ id: string }>('/api/places/suggestions', { method: 'POST', body: JSON.stringify(body) }),

  routes: () => request<RouteSummary[]>('/api/routes'),

  route: (slug: string) => request<RouteDetail>(`/api/routes/${encodeURIComponent(slug)}`),

  geocode: (q: string) => request<GeocodeResult[]>(`/api/planner/geocode${qs({ q, limit: 6 })}`),

  planRoute: (body: { waypoints: Waypoint[]; corridorMiles: number; maxResults?: number } & CorridorFilters) =>
    request<PlannedRoute>('/api/planner/route', { method: 'POST', body: JSON.stringify(body) }),

  exploreArea: (
    body: { lat: number; lng: number; radiusMiles: number; loopStops?: number; maxResults?: number } & CorridorFilters,
  ) => request<AreaResult>('/api/planner/area', { method: 'POST', body: JSON.stringify(body) }),

  createTrip: (body: {
    name: string;
    kind: TripKind;
    /** Required (with the pledge) only for a bar hop. */
    designatedDriver?: string | null;
    driverPledged?: boolean;
    tripDate?: string | null;
    placeIds: string[];
    waypoints: Waypoint[];
    notes?: string;
  }) => request<{ code: string; url: string }>('/api/trips', { method: 'POST', body: JSON.stringify(body) }),

  trip: (code: string) => request<SharedTrip>(`/api/trips/${encodeURIComponent(code)}`),

  shareTrip: (code: string, body: { senderName: string; recipients: string[]; message?: string }) =>
    request<{ sent: number }>(`/api/trips/${encodeURIComponent(code)}/share`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // The claim keeps the API's original field names (barId, barName) for any kind of place.
  claimListing: (body: {
    barId?: string;
    barName: string;
    town?: string;
    state?: string;
    ownerName: string;
    email: string;
    phone?: string;
    ddPerk?: string;
    message?: string;
  }) => request<{ id: string }>('/api/owners/claims', { method: 'POST', body: JSON.stringify(body) }),
};
